import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "sonner";
import { MapPin, Ship, Plane, Box } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function FreightTab({ freightQuotes, orders, isLoading, getStatusBadge, fetchAllData }) {
  const [editingFreight, setEditingFreight] = useState(null);
  const [editPrice, setEditPrice] = useState("");
  const [editDays, setEditDays] = useState("");
  const [editCarrier, setEditCarrier] = useState("");
  const [editTracking, setEditTracking] = useState("");
  const [editStatus, setEditStatus] = useState("");

  const updateFreightQuote = async (freightId) => {
    try {
      const updates = {};
      if (editPrice) updates.quoted_price = parseFloat(editPrice);
      if (editDays) updates.estimated_days = parseInt(editDays);
      if (editCarrier) updates.carrier = editCarrier;
      if (editTracking) updates.tracking_number = editTracking;
      if (editStatus) updates.status = editStatus;
      await axios.patch(`${API}/freight/quotes/${freightId}`, updates);
      toast.success("Updated"); setEditingFreight(null); fetchAllData();
    } catch (e) { toast.error("Failed"); }
  };

  const getOrder = (orderId) => orders.find(o => o.id === orderId);

  if (isLoading) return <div className="text-center py-12 text-zinc-500">Loading...</div>;
  if (freightQuotes.length === 0) return <div className="text-center py-12 text-zinc-500 bg-white rounded-sm border">No freight quotes yet</div>;

  return (
    <div className="bg-white rounded-sm border border-zinc-200 overflow-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            <TableHead className="font-heading uppercase text-xs">Order</TableHead>
            <TableHead className="font-heading uppercase text-xs">Route</TableHead>
            <TableHead className="font-heading uppercase text-xs">Method</TableHead>
            <TableHead className="font-heading uppercase text-xs">Status</TableHead>
            <TableHead className="font-heading uppercase text-xs">Quote</TableHead>
            <TableHead className="font-heading uppercase text-xs">Tracking</TableHead>
            <TableHead className="font-heading uppercase text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {freightQuotes.map((f) => (
            <TableRow key={f.id}>
              <TableCell className="font-mono text-sm">{getOrder(f.order_id)?.order_number || "N/A"}</TableCell>
              <TableCell>
                <p className="text-sm flex items-center gap-1"><MapPin className="h-3 w-3" /> {f.origin}</p>
                <p className="text-sm text-zinc-500">→ {f.destination}</p>
              </TableCell>
              <TableCell className="capitalize">
                {f.shipping_method === "sea" ? <Ship className="h-4 w-4 inline mr-1" /> : f.shipping_method === "air" ? <Plane className="h-4 w-4 inline mr-1" /> : <Box className="h-4 w-4 inline mr-1" />}
                {f.shipping_method}
              </TableCell>
              <TableCell><Badge className={getStatusBadge(f.status)}>{f.status}</Badge></TableCell>
              <TableCell>{f.quoted_price ? <span className="font-medium text-green-600">${f.quoted_price.toFixed(2)}</span> : <span className="text-zinc-400">Pending</span>}</TableCell>
              <TableCell>{f.tracking_number || <span className="text-zinc-400">—</span>}</TableCell>
              <TableCell>
                <Dialog open={editingFreight === f.id} onOpenChange={(open) => { setEditingFreight(open ? f.id : null); if (open) { setEditPrice(f.quoted_price?.toString() || ""); setEditDays(f.estimated_days?.toString() || ""); setEditCarrier(f.carrier || ""); setEditTracking(f.tracking_number || ""); setEditStatus(f.status); } }}>
                  <DialogTrigger asChild><Button variant="outline" size="sm">Edit</Button></DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading uppercase">Update Freight</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase">Price ($)</Label>
                          <Input type="number" value={editPrice} onChange={(e) => setEditPrice(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Days</Label>
                          <Input type="number" value={editDays} onChange={(e) => setEditDays(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Carrier</Label>
                          <Input value={editCarrier} onChange={(e) => setEditCarrier(e.target.value)} className="mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase">Status</Label>
                          <Select value={editStatus} onValueChange={setEditStatus}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="requested">Requested</SelectItem>
                              <SelectItem value="quoted">Quoted</SelectItem>
                              <SelectItem value="booked">Booked</SelectItem>
                              <SelectItem value="in_transit">In Transit</SelectItem>
                              <SelectItem value="delivered">Delivered</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs uppercase">Tracking #</Label>
                          <Input value={editTracking} onChange={(e) => setEditTracking(e.target.value)} className="mt-1" />
                        </div>
                      </div>
                      <Button onClick={() => updateFreightQuote(f.id)} className="w-full bg-blue-600 hover:bg-blue-700">Save</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
